import {
  FormEvent,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
  useRef,
} from "react";
import { AuthApiError } from "@supabase/supabase-js";
import { TeacherApp } from "./TeacherApp";
import { ensureAnonymousSession, supabase } from "./supabaseClient";
import type { Room, Participant, Poll, PollOption } from "./types";
import { isOtherOption, displayOptionLabel, sortPollsByOrder } from "./types";
import {
  HandIcon,
  MessageIcon,
  ChartIcon,
  HourglassIcon,
  CheckCircleIcon,
  SendIcon,
  SquareIcon,
  CheckSquareIcon,
} from "./icons";
import { LegalModal } from "./LegalModal";
import { TermsOfService, PrivacyPolicy } from "./LegalContent";
import { renderTextWithLinks } from "./linkify";

type StudentView = "waiting" | "questions" | "chat" | "polls";

// 환경 변수 검증
function useAnonymousAuth(enabled = true) {
  const [ready, setReady] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled) {
      setReady(true);
      return;
    }

    let cancelled = false;

    const ensureSession = async () => {
      try {
        await ensureAnonymousSession();
        if (!cancelled) {
          setReady(true);
        }
      } catch (authError) {
        if (!cancelled) {
          if (
            authError instanceof AuthApiError &&
            authError.message.includes("Anonymous sign-ins are disabled")
          ) {
            setError(
              "익명 로그인이 꺼져 있어요. Supabase 대시보드 → Authentication → Providers → Anonymous에서 기능을 켜주세요."
            );
          } else {
            setError(
              `Supabase 인증에 실패했습니다: ${
                authError instanceof Error ? authError.message : "알 수 없는 오류"
              }`
            );
          }
        }
      }
    };

    ensureSession();

    return () => {
      cancelled = true;
    };
  }, [enabled]);

  return { ready, error };
}

function validateRoom(room: Room) {
  if (room.status === "deleted" || room.deleted_at) {
    throw new Error(
      "이 방은 삭제됐거나 종료됐어요. 교사에게 새 코드를 요청해주세요."
    );
  }

  const expiresAt = new Date(room.expires_at).getTime();
  if (Number.isFinite(expiresAt) && expiresAt <= Date.now()) {
    throw new Error(
      "방이 만료됐어요. 교사에게 새 코드를 요청해주세요."
    );
  }
}

export default function App() {
  const [code, setCode] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [participant, setParticipant] = useState<Participant | null>(null);
  const [room, setRoom] = useState<Room | null>(null);
  const [currentView, setCurrentView] = useState<StudentView>("waiting");
  const [activeFeatures, setActiveFeatures] = useState<Set<string>>(new Set());
  const [legalModal, setLegalModal] = useState<"tos" | "privacy" | null>(null);

  const urlParams = new URLSearchParams(window.location.search);
  const isSidepanel = urlParams.get("mode") === "sidepanel";
  const isTeacher = urlParams.get("role") === "teacher";
  const isAnon = urlParams.get("anon") === "true";
  const availableRoles = urlParams.get("roles") ? urlParams.get("roles")!.split(",") : ["학생"];

  const { ready, error: authError } = useAnonymousAuth(!isTeacher);

  // iframe 모드일 때 스타일 적용
  useEffect(() => {
    if (isSidepanel) {
      document.documentElement.classList.add("iframe-mode");
      document.body.classList.add("iframe-mode");
    }
    return () => {
      document.documentElement.classList.remove("iframe-mode");
      document.body.classList.remove("iframe-mode");
    };
  }, [isSidepanel]);

  // 세션 복구
  useEffect(() => {
    if (!ready) return;

    const savedSession = localStorage.getItem("classlive-student-session");
    if (savedSession) {
      try {
        const {
          roomId,
          participantId,
          displayName: savedName,
        } = JSON.parse(savedSession);

        // 저장된 세션으로 방과 참여자 정보 복구
        const restoreSession = async () => {
          try {
            setLoading(true);

            const { data: roomData, error: roomError } = await supabase
              .from("rooms")
              .select("id, code, title, host_user_id, status, expires_at, deleted_at, active_feature")
              .eq("id", roomId)
              .maybeSingle();

            console.log("[ClassLive Debug] Fetched room data on session restore:", roomData);

            if (roomError || !roomData) {
              throw new Error("방을 찾지 못했어요. 코드를 다시 확인해주세요.");
            }

            validateRoom(roomData as Room);

            const { data: participantData, error: participantError } =
              await supabase
                .from("room_participants")
                .select("*")
                .eq("id", participantId)
                .maybeSingle();

            if (participantError || !participantData) {
              throw new Error("참여자 정보를 찾지 못했어요. 다시 입장해주세요.");
            }

            const restoredRoom = roomData as Room;
            setRoom(restoredRoom);
            setParticipant(participantData as Participant);
            setDisplayName(savedName);

            if (restoredRoom.active_feature) {
              const feature = restoredRoom.active_feature as StudentView;
              setCurrentView(feature);
              // 현재 활성화된 기능이 여러개일 수 있으므로, Set에 추가합니다.
              setActiveFeatures(prev => new Set([...prev, feature]));
            } else {
              setCurrentView("waiting");
            }
          } catch (restoreError) {
            console.error("세션 복구 실패:", restoreError);
            localStorage.removeItem("classlive-student-session");
            setError("이전 세션을 복구하지 못했어요. 다시 입장해주세요.");
          } finally {
            setLoading(false);
          }
        };

        restoreSession();
      } catch (parseError) {
        console.error("세션 데이터 파싱 실패:", parseError);
        localStorage.removeItem("classlive-student-session");
      }
    }
  }, [ready]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const initialCode = params.get("code");
    if (initialCode) {
      const sanitized = initialCode.replace(/\D/g, "").slice(0, 6);
      setCode(sanitized);
    }
  }, []);

  useEffect(() => {
    setError(authError);
  }, [authError]);

  const handleJoin = useCallback(
    async (event?: FormEvent<HTMLFormElement>, selectedRole?: string) => {
      if (event) {
        event.preventDefault();
      }
      setError(null);
      setParticipant(null);
      setRoom(null);

      const sanitizedCode = code.replace(/\D/g, "").slice(0, 6);
      let trimmedName = displayName.trim();

      if (!/^\d{6}$/.test(sanitizedCode)) {
        setError("방 코드는 숫자 6자리여야 합니다.");
        return;
      }

      if (!selectedRole && trimmedName.length === 0) {
        setError("닉네임을 입력해주세요.");
        return;
      }

      setLoading(true);
      try {
        const { data: roomData, error: roomError } = await supabase
          .from("rooms")
          .select("id, code, title, host_user_id, status, expires_at, deleted_at, active_feature")
          .eq("code", sanitizedCode)
          .maybeSingle();

        console.log("[ClassLive Debug] Fetched room data on new join:", roomData);

        if (roomError) {
          throw roomError;
        }

        if (!roomData) {
          throw new Error("해당 코드의 방을 찾을 수 없습니다.");
        }

        validateRoom(roomData as Room);

        const joinedRoom = roomData as Room;

        let participantRow: any;

        // 같은 이름의 참여자가 이미 있는지 확인 (선택된 역할이 없을 때만)
        if (!selectedRole) {
          const { data: existingParticipants, error: checkError } = await supabase
            .from("room_participants")
            .select("id, display_name, left_at")
            .eq("room_id", joinedRoom.id)
            .eq("display_name", trimmedName);

          if (checkError) {
            console.error("이름 중복 체크 오류:", checkError);
          } else if (
            existingParticipants &&
            existingParticipants.some((participant) => !participant.left_at)
          ) {
            throw new Error(
              `'${trimmedName}' 닉네임은 이미 사용 중입니다. 다른 닉네임을 사용해주세요.`
            );
          }

          const payload = {
            room_id: joinedRoom.id,
            display_name: trimmedName,
            role: "participant" as const,
            profile_id: (await supabase.auth.getUser()).data.user?.id ?? null,
          };

          const { data: insertedParticipant, error: joinError } = await supabase
            .from("room_participants")
            .insert(payload)
            .select("id, room_id, display_name, joined_at")
            .single();

          if (joinError) throw joinError;
          participantRow = insertedParticipant;

        } else {
          // 익명 참여: 가입 후 교사(호스트) 페이지에서 번호 부여를 기다림
          const tempPayload = {
            room_id: joinedRoom.id,
            display_name: selectedRole,
            role: "participant" as const,
            profile_id: (await supabase.auth.getUser()).data.user?.id ?? null,
          };

          const { data: tempParticipant, error: tempJoinError } = await supabase
            .from("room_participants")
            .insert(tempPayload)
            .select("id, room_id, display_name, joined_at")
            .single();

          if (tempJoinError) throw tempJoinError;
          participantRow = tempParticipant;

          // 호스트가 이름을 변경해줄 때까지 주기적으로 확인 (최대 10초 대기)
          let finalName = selectedRole;
          let attempts = 0;
          while (finalName === selectedRole && attempts < 20) {
            await new Promise((resolve) => setTimeout(resolve, 500));
            const { data } = await supabase
              .from("room_participants")
              .select("display_name")
              .eq("id", participantRow.id)
              .single();

            if (data && data.display_name !== selectedRole) {
              finalName = data.display_name;
            }
            attempts++;
          }

          // 시간 초과 시 랜덤 문자열 추가 (호스트 미접속 등의 상황 대비)
          if (finalName === selectedRole) {
            const randomSuffix = Math.floor(100 + Math.random() * 900);
            finalName = `${selectedRole} ${randomSuffix}`;
            await supabase
              .from("room_participants")
              .update({ display_name: finalName })
              .eq("id", participantRow.id);
          }

          trimmedName = finalName;
          participantRow.display_name = trimmedName;
          setDisplayName(trimmedName);
        }

        setRoom(joinedRoom);
        setParticipant(participantRow as Participant);

        if (joinedRoom.active_feature) {
          const feature = joinedRoom.active_feature as StudentView;
          setCurrentView(feature);
          setActiveFeatures(new Set([feature]));
        } else {
          setCurrentView("waiting");
        }

        // 세션 저장
        localStorage.setItem(
          "classlive-student-session",
          JSON.stringify({
            roomId: roomData.id,
            participantId: participantRow.id,
            displayName: trimmedName,
          })
        );
      } catch (joinError: unknown) {
        console.error(joinError);
        if (joinError instanceof Error) {
          setError(joinError.message);
        } else {
          setError("방에 들어가지 못했어요. 다시 시도해주세요.");
        }
      } finally {
        setLoading(false);
      }
    },
    [code, displayName]
  );

  // 실시간 기능 상태 구독
  useEffect(() => {
    if (!room || !participant) return;

    //console.log("구독 시작:", `room-${room.id}`);

    const channel = supabase
      .channel(`room-${room.id}`)
      .on("broadcast", { event: "feature_started" }, (payload) => {
        //console.log("기능 시작 수신:", payload);
        const { feature } = payload.payload as { feature: string };
        setActiveFeatures((prev) => new Set([...prev, feature]));
        setCurrentView(feature as StudentView);
      })
      .on("broadcast", { event: "feature_stopped" }, (payload) => {
        //console.log("기능 중지 수신:", payload);
        const { feature } = payload.payload as { feature: string };
        setActiveFeatures((prev) => {
          const next = new Set(prev);
          next.delete(feature);
          return next;
        });
        if (currentView === feature) {
          setCurrentView("waiting");
        }
      })
      .subscribe((status) => {
        //console.log("구독 상태:", status);
      });

    return () => {
      //console.log("구독 해제");
      supabase.removeChannel(channel);
    };
  }, [room, participant, currentView]);

  const successMessage = useMemo(() => {
    if (!participant || !room) return null;
    const timestamp = new Date().toLocaleString("ko-KR");
    return `"${room.title ?? `교실 (${room.code})`}" 방에 '${participant.display_name
      }' 닉네임으로 입장했습니다. (${timestamp})`;
  }, [participant, room]);

  const handleLeaveClassroom = useCallback(async () => {
    if (!room || !participant) return;
    if (!window.confirm("교실을 나갈까요?")) {
      return;
    }

    const leftAt = new Date().toISOString();
    try {
      const { error } = await supabase
        .from("room_participants")
        .update({ left_at: leftAt })
        .eq("id", participant.id);

      if (error) {
        console.error("참가자 퇴장 처리 실패 (update):", error);
        const { error: deleteError } = await supabase
          .from("room_participants")
          .delete()
          .eq("id", participant.id);
        if (deleteError) {
          console.error("참가자 퇴장 삭제도 실패:", deleteError);
        }
      }
    } catch (leaveError) {
      console.error("참가자 퇴장 처리 중 오류:", leaveError);
    } finally {
      localStorage.removeItem("classlive-student-session");
      setParticipant(null);
      setRoom(null);
      setCurrentView("waiting");
      setActiveFeatures(new Set());
      setCode("");
      setDisplayName("");
      setError(null);
    }
  }, [participant, room]);

  // 교사 모드
  if (isTeacher) {
    return <TeacherApp />;
  }

  // 입장 후 학생 화면
  if (participant && room) {
    return (
      <StudentClassroom
        room={room}
        participant={participant}
        currentView={currentView}
        activeFeatures={activeFeatures}
        onLeave={handleLeaveClassroom}
      />
    );
  }

  // 입장 전 화면
  return (
    <main className="student-app">
      <header className="student-header">
        <h1>ClassLive</h1>
        <p>수업 참여</p>
      </header>

      <section className="student-card">
        {!ready ? <p className="student-loading">연결 중...</p> : null}
        {error ? <div className="student-alert">{error}</div> : null}
        {successMessage ? (
          <div className="student-success">{successMessage}</div>
        ) : null}

        <form onSubmit={handleJoin}>
          <div className="student-form-group">
            <input
              className="student-input"
              placeholder="6자리 코드"
              value={code}
              onChange={(event) => setCode(event.target.value.toUpperCase())}
              maxLength={6}
              required
            />
          </div>

          {!isAnon ? (
            <>
              <div className="student-form-group">
                <input
                  className="student-input"
                  placeholder="닉네임"
                  value={displayName}
                  onChange={(event) => setDisplayName(event.target.value)}
                  required
                />
              </div>
              <button
                className="student-button"
                type="submit"
                disabled={!ready || loading}
              >
                {loading ? "입장 중..." : "입장하기"}
              </button>
            </>
          ) : availableRoles.length === 1 ? (
            <div className="student-anon-roles">
              <p className="student-anon-desc" style={{ textAlign: "center" }}>
                '{availableRoles[0]}'(으)로 입장합니다.
              </p>
              <button
                className="student-button"
                type="button"
                style={{ marginTop: "12px" }}
                onClick={() => handleJoin(undefined, availableRoles[0])}
                disabled={!ready || loading}
              >
                {loading ? "입장 중..." : "입장하기"}
              </button>
            </div>
          ) : (
            <div className="student-anon-roles">
              <p className="student-anon-desc">입장할 역할을 선택해주세요.</p>
              <div className="student-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px", marginTop: "12px" }}>
                {availableRoles.map((role) => (
                  <button
                    key={role}
                    type="button"
                    className="student-button"
                    style={{ margin: 0 }}
                    onClick={() => handleJoin(undefined, role)}
                    disabled={!ready || loading}
                  >
                    {role}
                  </button>
                ))}
              </div>
            </div>
          )}
        </form>
      </section>
      <footer className="copyright-footer">
        <span>Copyright © 2025{new Date().getFullYear() > 2025 ? `-${new Date().getFullYear()}` : ''} SsamFlow. All rights reserved.</span>
        <div className="legal-links">
          <button type="button" onClick={() => setLegalModal("tos")}>
            이용약관
          </button>
          <button type="button" onClick={() => setLegalModal("privacy")}>
            개인정보처리방침
          </button>
        </div>
      </footer>

      <LegalModal
        isOpen={legalModal === "tos"}
        onClose={() => setLegalModal(null)}
        title="이용약관"
      >
        <TermsOfService />
      </LegalModal>

      <LegalModal
        isOpen={legalModal === "privacy"}
        onClose={() => setLegalModal(null)}
        title="개인정보처리방침"
      >
        <PrivacyPolicy />
      </LegalModal>
    </main>
  );
}

function StudentClassroom({
  room,
  participant,
  currentView,
  activeFeatures,
  onLeave,
}: {
  room: Room;
  participant: Participant;
  currentView: StudentView;
  activeFeatures: Set<string>;
  onLeave: () => void;
}) {
  const renderCurrentView = () => {
    switch (currentView) {
      case "questions":
        return <StudentQuestions room={room} participant={participant} />;
      case "chat":
        return <StudentChat room={room} participant={participant} />;
      case "polls":
        return <StudentPolls room={room} participant={participant} />;
      default:
        return <StudentWaiting activeFeatures={activeFeatures} />;
    }
  };

  return (
    <main className="student-classroom">
      <header className="student-classroom-header">
        <h1>{room.title || `교실 (${room.code})`}</h1>
        <p>안녕하세요, {participant.display_name}님!</p>
        <button className="student-leave-button" type="button" onClick={onLeave}>
          교실 나가기
        </button>
      </header>

      <div className="student-classroom-content">{renderCurrentView()}</div>
      <div className="copyright-footer">
        Copyright © 2025{new Date().getFullYear() > 2025 ? `-${new Date().getFullYear()}` : ''} SsamFlow. All rights reserved.
      </div>
    </main>
  );
}

function StudentWaiting({ activeFeatures }: { activeFeatures: Set<string> }) {
  return (
    <div className="student-waiting">
      <div className="student-waiting-icon">
        <HourglassIcon size={52} strokeWidth={1.8} />
      </div>
      <h2>대기 중...</h2>
      <p>교사가 수업을 시작할 때까지 기다리고 있어요.</p>

      {activeFeatures.size > 0 && (
        <div className="student-active-features">
          <h3>현재 활성화된 기능:</h3>
          <ul>
            {Array.from(activeFeatures).map((feature) => (
              <li key={feature}>
                {feature === "questions" && (
                  <>
                    <HandIcon size={15} /> 질문
                  </>
                )}
                {feature === "chat" && (
                  <>
                    <MessageIcon size={15} /> 채팅
                  </>
                )}
                {feature === "polls" && (
                  <>
                    <ChartIcon size={15} /> 설문
                  </>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function StudentQuestions({
  room,
  participant,
}: {
  room: Room;
  participant: Participant;
}) {
  const [questionText, setQuestionText] = useState("");
  const [questions, setQuestions] = useState<
    Array<{
      id: string;
      body: string;
      status: string;
      created_at: string;
    }>
  >([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let isCancelled = false;

    const fetchQuestions = async () => {
      const { data, error } = await supabase
        .from("room_questions")
        .select("*")
        .eq("room_id", room.id)
        .eq("author_id", participant.id)
        .order("created_at", { ascending: false });

      if (error) {
        console.error("질문 목록 조회 실패:", error);
        return;
      }

      if (!isCancelled) {
        setQuestions(
          (data ?? []).map((row) => ({
            id: row.id,
            body: row.body,
            status: row.status,
            created_at: row.created_at,
          }))
        );
      }
    };

    fetchQuestions();

    const channel = supabase
      .channel(`student-questions-${room.id}-${participant.id}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "room_questions",
          filter: `room_id=eq.${room.id}`,
        },
        fetchQuestions
      )
      .subscribe();

    return () => {
      isCancelled = true;
      supabase.removeChannel(channel);
    };
  }, [room.id, participant.id]);

  const handleSubmitQuestion = async (e: React.FormEvent) => {
    e.preventDefault();
    const body = questionText.trim();
    if (!body || submitting) return;

    setSubmitting(true);
    try {
      const { data, error } = await supabase
        .from("room_questions")
        .insert({
          room_id: room.id,
          author_id: participant.id,
          author_name: participant.display_name,
          body,
          status: "open",
        })
        .select("*")
        .single();

      if (error) throw error;

      if (data) {
        setQuestions((prev) => [
          {
            id: data.id,
            body: data.body,
            status: data.status,
            created_at: data.created_at,
          },
          ...prev.filter((item) => item.id !== data.id),
        ]);
      }

      setQuestionText("");
    } catch (error) {
      console.error("질문 제출 실패:", error);
    } finally {
      setSubmitting(false);
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case "answered":
        return "답변 완료";
      case "archived":
        return "보관됨";
      default:
        return "답변 대기 중";
    }
  };

  return (
    <div className="student-feature">
      <div className="feature-header">
        <h2>
          <HandIcon size={22} /> 질문
        </h2>
        <p>궁금한 점을 물어보세요!</p>
      </div>

      <form onSubmit={handleSubmitQuestion} className="student-question-form">
        <textarea
          value={questionText}
          onChange={(e) => setQuestionText(e.target.value)}
          placeholder="질문을 입력하세요..."
          className="student-question-input"
          rows={4}
        />
        <button
          type="submit"
          className="student-submit-button"
          disabled={!questionText.trim() || submitting}
        >
          {submitting ? "전송 중..." : "질문 올리기"}
        </button>
      </form>

      <div className="student-questions-list">
        <h3>내가 올린 질문</h3>
        {questions.length === 0 ? (
          <p className="empty-state">아직 올린 질문이 없어요.</p>
        ) : (
          questions.map((question) => (
            <div key={question.id} className="student-question-item">
              <p>{question.body}</p>
              <span className={`question-status status-${question.status}`}>
                {getStatusLabel(question.status)}
              </span>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function StudentChat({
  room,
  participant,
}: {
  room: Room;
  participant: Participant;
}) {
  const [messageText, setMessageText] = useState("");
  const [messages, setMessages] = useState<
    Array<{
      id: string;
      senderId: string;
      senderName: string;
      body: string;
      visibility: string;
      directTargetId: string | null;
      created_at: string;
    }>
  >([]);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let isCancelled = false;

    const normalizeMessage = (row: any) => ({
      id: row.id,
      senderId: row.sender_id,
      senderName: row.sender_name,
      body: row.body,
      visibility: row.visibility,
      directTargetId: row.direct_target_id ?? null,
      created_at: row.created_at,
    });

    const fetchMessages = async () => {
      const { data, error } = await supabase
        .from("room_messages")
        .select("*")
        .eq("room_id", room.id)
        .order("created_at", { ascending: true });

      if (error) {
        console.error("메시지 목록 조회 실패:", error);
        return;
      }

      const visible = (data ?? []).filter((row) => {
        if (row.visibility === "public") return true;
        if (row.visibility === "direct") {
          return (
            row.direct_target_id === participant.id ||
            row.sender_id === participant.id
          );
        }
        return false;
      });

      if (!isCancelled) {
        setMessages(visible.map(normalizeMessage));
      }
    };

    fetchMessages();

    const channel = supabase
      .channel(`student-chat-${room.id}-${participant.id}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "room_messages",
          filter: `room_id=eq.${room.id}`,
        },
        fetchMessages
      )

      .subscribe((status) => {
        if (status === "SUBSCRIBED") {
          //console.log("학생 앱 채팅: 실시간 구독 성공");
        } else if (status === "CHANNEL_ERROR" || status === "TIMED_OUT") {
          console.error("학생 앱 채팅: 실시간 구독 실패:", status);
        }
      });

    // 백업 폴링 (실시간 구독이 실패할 경우 대비)
    const messageInterval = setInterval(() => {
      if (!isCancelled) {
        fetchMessages();
      }
    }, 3000); // 3초마다 폴링

    return () => {
      isCancelled = true;
      clearInterval(messageInterval);
      supabase.removeChannel(channel);
    };
  }, [room.id, participant.id]);

  // 자동 스크롤
  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    const body = messageText.trim();
    if (!body || sending) return;

    setSending(true);
    try {
      const { data, error } = await supabase
        .from("room_messages")
        .insert({
          room_id: room.id,
          sender_id: participant.id,
          sender_name: participant.display_name,
          body,
          visibility: "public",
        })
        .select("*")
        .single();

      if (error) throw error;

      if (data) {
        setMessages((prev) => [
          ...prev,
          {
            id: data.id,
            senderId: data.sender_id,
            senderName: data.sender_name,
            body: data.body,
            visibility: data.visibility,
            directTargetId: data.direct_target_id ?? null,
            created_at: data.created_at,
          },
        ]);
      }

      setMessageText("");
    } catch (error) {
      console.error("메시지 전송 실패:", error);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="student-feature">
      <div className="feature-header">
        <h2>
          <MessageIcon size={22} /> 채팅
        </h2>
        <p>다른 학생들과 소통해보세요!</p>
      </div>

      <div className="student-chat-container">
        <div className="student-messages">
          {messages.length === 0 ? (
            <p className="empty-state">아직 메시지가 없습니다.</p>
          ) : (
            messages.map((message) => {
              const isOwnMessage = message.senderId === participant.id;
              return (
                <div
                  key={message.id}
                  className={`classlive-message-bubble ${isOwnMessage ? "own-message" : "other-message"
                    }`}
                >
                  <div className="classlive-message-content">
                    <div className="classlive-message-header">
                      <span className="classlive-message-author">
                        {message.senderName}
                      </span>
                      <span className="classlive-message-time">
                        {new Date(message.created_at).toLocaleTimeString(
                          "ko-KR",
                          {
                            hour: "2-digit",
                            minute: "2-digit",
                          }
                        )}
                      </span>
                    </div>
                    <div className="classlive-message-body">
                      {renderTextWithLinks(message.body ?? "", message.id)}
                    </div>
                  </div>
                </div>
              );
            })
          )}
          <div ref={messagesEndRef} />
        </div>

        <form onSubmit={handleSendMessage} className="student-message-form">
          <input
            type="text"
            value={messageText}
            onChange={(e) => setMessageText(e.target.value)}
            placeholder="메시지를 입력하세요..."
            className="student-message-input"
          />
          <button
            type="submit"
            className="student-send-button"
            disabled={!messageText.trim() || sending}
          >
            {sending ? (
              "전송 중..."
            ) : (
              <>
                <SendIcon size={15} /> 전송
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  );
}

function StudentPolls({
  room,
  participant,
}: {
  room: Room;
  participant: Participant;
}) {
  const [textDrafts, setTextDrafts] = useState<Record<string, string>>({});
  const [wordDrafts, setWordDrafts] = useState<Record<string, string>>({});
  const [otherDrafts, setOtherDrafts] = useState<Record<string, string>>({});
  const [checkDrafts, setCheckDrafts] = useState<Record<string, string[]>>({});
  const [polls, setPolls] = useState<Poll[]>([]);
  const [myResponses, setMyResponses] = useState<Set<string>>(new Set());

  // 설문 데이터 실시간 구독
  useEffect(() => {
    let isCancelled = false;

    const fetchPolls = async () => {
      const { data, error } = await supabase
        .from("room_polls")
        .select(
          `
          *,
          room_poll_options (*)
        `
        )
        .eq("room_id", room.id)
        .order("created_at", { ascending: true });

      if (error) {
        console.error("설문 목록 조회 실패:", error);
        return;
      }

      if (!isCancelled) {
        const pollsWithOptions = (data || [])
          .filter((poll) => poll.started_at !== null)
          .map((poll) => ({
            ...poll,
            options: (poll.room_poll_options || []).slice().sort(
              (a: PollOption, b: PollOption) => a.order_index - b.order_index
            ),
          }));
        setPolls(sortPollsByOrder(pollsWithOptions as Poll[]));
      }
    };

    const fetchMyResponses = async () => {
      const { data, error } = await supabase
        .from("room_poll_responses")
        .select("poll_id")
        .eq("participant_id", participant.id);

      if (error) {
        console.error("내 응답 조회 실패:", error);
        return;
      }

      if (!isCancelled) {
        setMyResponses(new Set((data || []).map((r) => r.poll_id)));
      }
    };

    fetchPolls();
    fetchMyResponses();

    const channel = supabase
      .channel(`student-polls-${room.id}-${participant.id}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "room_polls",
          filter: `room_id=eq.${room.id}`,
        },
        fetchPolls
      )
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "room_poll_responses",
          filter: `participant_id=eq.${participant.id}`,
        },
        fetchMyResponses
      )

      .subscribe((status) => {
        if (status === "SUBSCRIBED") {
          //console.log("학생 앱 설문: 실시간 구독 성공");
        } else if (status === "CHANNEL_ERROR" || status === "TIMED_OUT") {
          console.error("학생 앱 설문: 실시간 구독 실패:", status);
        }
      });

    // 백업 폴링 (실시간 구독이 실패할 경우 대비)
    const pollInterval = setInterval(() => {
      if (!isCancelled) {
        fetchPolls();
        fetchMyResponses();
      }
    }, 5000); // 5초마다 폴링

    return () => {
      isCancelled = true;
      clearInterval(pollInterval);
      supabase.removeChannel(channel);
    };
  }, [room.id, participant.id]);

  // 활성 설문 찾기 (종료되지 않은 설문)
  const activePolls = sortPollsByOrder(
    polls.filter((poll) => poll.started_at && !poll.closed_at)
  );
  const closedPolls = sortPollsByOrder(
    polls.filter((poll) => poll.started_at && poll.closed_at)
  );

  const handleOptionSelect = async (pollId: string, optionId: string) => {
    if (!room || !participant) return;

    try {
      const { error } = await supabase.from("room_poll_responses").insert({
        poll_id: pollId,
        participant_id: participant.id,
        answer: optionId,
      });

      if (error) throw error;

      setMyResponses((prev) => new Set([...prev, pollId]));
    } catch (error) {
      console.error("설문 응답 실패:", error);
      alert("설문 응답을 보내지 못했어요. 다시 시도해주세요.");
    }
  };

  const handleOtherSubmit = async (pollId: string, otherOptionId: string) => {
    if (!room || !participant) return;
    const draft = otherDrafts[pollId]?.trim();
    if (!draft) {
      alert("기타 내용을 입력해주세요.");
      return;
    }

    try {
      const { error } = await supabase.from("room_poll_responses").insert({
        poll_id: pollId,
        participant_id: participant.id,
        answer: [otherOptionId, { otherText: draft }],
      });

      if (error) throw error;

      setMyResponses((prev) => new Set([...prev, pollId]));
      setOtherDrafts((current) => {
        const next = { ...current };
        delete next[pollId];
        return next;
      });
    } catch (error) {
      console.error("설문 응답 실패:", error);
      alert("설문 응답에 실패했습니다. 다시 시도해주세요.");
    }
  };

  const handleCheckboxToggle = (pollId: string, optionId: string) => {
    setCheckDrafts((current) => {
      const selected = current[pollId] ?? [];
      const next = selected.includes(optionId)
        ? selected.filter((id) => id !== optionId)
        : [...selected, optionId];
      return { ...current, [pollId]: next };
    });
  };

  const handleCheckboxSubmit = async (
    pollId: string,
    otherOptionId: string | null
  ) => {
    if (!room || !participant) return;
    const selected = checkDrafts[pollId] ?? [];
    if (selected.length === 0) {
      alert("하나 이상의 항목을 선택해주세요.");
      return;
    }

    const otherSelected = otherOptionId && selected.includes(otherOptionId);
    const otherText = otherDrafts[pollId]?.trim();
    if (otherSelected && !otherText) {
      alert("기타 내용을 입력해주세요.");
      return;
    }

    const answer: unknown[] = [...selected];
    if (otherSelected && otherText) {
      answer.push({ otherText });
    }

    try {
      const { error } = await supabase.from("room_poll_responses").insert({
        poll_id: pollId,
        participant_id: participant.id,
        answer,
      });

      if (error) throw error;

      setMyResponses((prev) => new Set([...prev, pollId]));
      setCheckDrafts((current) => {
        const next = { ...current };
        delete next[pollId];
        return next;
      });
      setOtherDrafts((current) => {
        const next = { ...current };
        delete next[pollId];
        return next;
      });
    } catch (error) {
      console.error("설문 응답 실패:", error);
      alert("설문 응답에 실패했습니다. 다시 시도해주세요.");
    }
  };

  const handleTextSubmit = async (pollId: string) => {
    if (!room || !participant) return;
    const draft = textDrafts[pollId]?.trim();
    if (!draft) return;

    try {
      const { error } = await supabase.from("room_poll_responses").insert({
        poll_id: pollId,
        participant_id: participant.id,
        answer: draft,
      });

      if (error) throw error;

      setMyResponses((prev) => new Set([...prev, pollId]));
      setTextDrafts((current) => {
        const next = { ...current };
        delete next[pollId];
        return next;
      });
    } catch (error) {
      console.error("설문 응답 실패:", error);
      alert("설문 응답에 실패했습니다. 다시 시도해주세요.");
    }
  };

  const handleWordSubmit = async (pollId: string) => {
    if (!room || !participant) return;
    const draft = wordDrafts[pollId]?.trim();
    if (!draft) {
      alert("단어를 입력해주세요.");
      return;
    }

    const sanitized = draft.split(/\s+/)[0];
    if (!sanitized) {
      alert("단어를 입력해주세요.");
      return;
    }
    if (sanitized.length > 30) {
      alert("단어는 30자 이내로 입력해주세요.");
      return;
    }

    try {
      const { error } = await supabase.from("room_poll_responses").insert({
        poll_id: pollId,
        participant_id: participant.id,
        answer: sanitized,
      });

      if (error) throw error;

      setMyResponses((prev) => new Set([...prev, pollId]));
      setWordDrafts((current) => {
        const next = { ...current };
        delete next[pollId];
        return next;
      });
    } catch (error) {
      console.error("설문 응답 실패:", error);
      alert("설문 응답에 실패했습니다. 다시 시도해주세요.");
    }
  };

  return (
    <div className="student-feature">
      <div className="feature-header">
        <h2>
          <ChartIcon size={22} /> 설문 조사
        </h2>
        <p>설문에 참여해주세요!</p>
      </div>

      {activePolls.length === 0 ? (
        <div className="no-poll">
          <p className="empty-state">지금 진행 중인 설문이 없어요.</p>
        </div>
      ) : (
        <div className="student-poll-stack">
          {activePolls.map((poll) => {
            const hasResponded = myResponses.has(poll.id);
            const sortedOptions = (poll.options || [])
              .slice()
              .sort((a, b) => a.order_index - b.order_index);
            const otherOption =
              sortedOptions.find((option) => isOtherOption(option.label)) ??
              null;

            return (
              <div key={poll.id} className="student-poll">
                <h3 className="poll-question">{poll.prompt}</h3>

                {hasResponded ? (
                  <div className="response-confirmation">
                    <p>
                      <CheckCircleIcon size={17} /> 응답이 완료되었습니다!
                    </p>
                    <p>다른 학생들의 응답을 기다리고 있어요...</p>
                  </div>
                ) : poll.type === "multiple_choice" ? (
                  <div className="poll-options">
                    {sortedOptions
                      .filter((option) => !isOtherOption(option.label))
                      .map((option) => (
                        <div
                          key={option.id}
                          className="poll-option"
                          role="button"
                          tabIndex={0}
                          onClick={() => {
                            if (hasResponded) return;
                            handleOptionSelect(poll.id, option.id);
                          }}
                          onKeyDown={(e) => {
                            if (hasResponded) return;
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              handleOptionSelect(poll.id, option.id);
                            }
                          }}
                        >
                          {option.label}
                        </div>
                      ))}
                    {otherOption && (
                      <div className="poll-option poll-option-other">
                        <span className="poll-option-other-label">기타</span>
                        <input
                          type="text"
                          className="poll-other-input"
                          placeholder="직접 입력하세요"
                          value={otherDrafts[poll.id] ?? ""}
                          onChange={(e) => {
                            const value = e.target.value;
                            setOtherDrafts((current) => ({
                              ...current,
                              [poll.id]: value,
                            }));
                          }}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              handleOtherSubmit(poll.id, otherOption.id);
                            }
                          }}
                        />
                        <button
                          type="button"
                          className="student-submit-button poll-other-submit"
                          disabled={!(otherDrafts[poll.id]?.trim())}
                          onClick={() =>
                            handleOtherSubmit(poll.id, otherOption.id)
                          }
                        >
                          제출
                        </button>
                      </div>
                    )}
                  </div>
                ) : poll.type === "checkbox" ? (
                  <div className="poll-options">
                    <p className="poll-checkbox-hint">
                      해당하는 항목을 모두 선택하세요.
                    </p>
                    {sortedOptions.map((option) => {
                      const selected = (checkDrafts[poll.id] ?? []).includes(
                        option.id
                      );
                      return (
                        <div key={option.id}>
                          <div
                            className={`poll-option poll-option-checkbox${selected ? " selected" : ""
                              }`}
                            role="checkbox"
                            aria-checked={selected}
                            tabIndex={0}
                            onClick={() =>
                              handleCheckboxToggle(poll.id, option.id)
                            }
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                handleCheckboxToggle(poll.id, option.id);
                              }
                            }}
                          >
                            <span className="poll-checkbox-mark">
                              {selected ? (
                                <CheckSquareIcon size={20} />
                              ) : (
                                <SquareIcon size={20} />
                              )}
                            </span>
                            {displayOptionLabel(option.label)}
                          </div>
                          {isOtherOption(option.label) && selected && (
                            <input
                              type="text"
                              className="poll-other-input"
                              placeholder="기타 내용을 입력하세요"
                              value={otherDrafts[poll.id] ?? ""}
                              onChange={(e) => {
                                const value = e.target.value;
                                setOtherDrafts((current) => ({
                                  ...current,
                                  [poll.id]: value,
                                }));
                              }}
                            />
                          )}
                        </div>
                      );
                    })}
                    <button
                      type="button"
                      className="student-submit-button"
                      disabled={(checkDrafts[poll.id] ?? []).length === 0}
                      onClick={() =>
                        handleCheckboxSubmit(poll.id, otherOption?.id ?? null)
                      }
                    >
                      응답 제출
                    </button>
                  </div>
                ) : poll.type === "open_text" ? (
                  <div className="text-poll">
                    <textarea
                      value={textDrafts[poll.id] ?? ""}
                      onChange={(e) => {
                        const value = e.target.value;
                        setTextDrafts((current) => ({
                          ...current,
                          [poll.id]: value,
                        }));
                      }}
                      placeholder="답변을 입력하세요..."
                      className="student-poll-textarea"
                      rows={4}
                      disabled={hasResponded}
                    />
                    <button
                      onClick={() => handleTextSubmit(poll.id)}
                      className="student-submit-button"
                      disabled={
                        hasResponded || !(textDrafts[poll.id]?.trim())
                      }
                    >
                      답변 제출
                    </button>
                  </div>
                ) : (
                  <div className="word-poll">
                    <input
                      type="text"
                      value={wordDrafts[poll.id] ?? ""}
                      onChange={(e) => {
                        const normalized = e.target.value
                          .trim()
                          .split(/\s+/)[0] ?? "";
                        setWordDrafts((current) => {
                          if (!normalized) {
                            const { [poll.id]: _removed, ...rest } = current;
                            return rest;
                          }
                          return { ...current, [poll.id]: normalized };
                        });
                      }}
                      placeholder="단어를 입력하세요"
                      className="student-word-input"
                      maxLength={30}
                      disabled={hasResponded}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          if (!hasResponded) {
                            handleWordSubmit(poll.id);
                          }
                        }
                      }}
                    />
                    <button
                      onClick={() => handleWordSubmit(poll.id)}
                      className="student-submit-button"
                      disabled={hasResponded || !(wordDrafts[poll.id]?.trim())}
                    >
                      단어 제출
                    </button>
                    <p className="student-word-hint">띄어쓰기 없이 한 단어로 입력해주세요.</p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {closedPolls.length > 0 && (
        <div className="completed-polls">
          <h4>완료된 설문 ({closedPolls.length}개)</h4>
          {closedPolls.map((poll) => (
            <div key={poll.id} className="completed-poll-item">
              <p>{poll.prompt}</p>
              <span className="poll-status">
                {myResponses.has(poll.id) ? "참여함" : "미참여"}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
